import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ContractResourceType, useContracts, useGlobalCounterparties, useMarginGroups } from "~/hooks/query";
import { useMapToId } from "~/hooks/util/useMapToId";
import { ContractType } from "~/types";

export function useGetContractLabel() {
  const { data: counterparties = [] } = useGlobalCounterparties();
  const idToCpMapping = useMapToId(counterparties);

  return (contract: ContractResourceType) => {
    const { contract_counterparties, id, name } = contract;
    const cpsLabel = `${idToCpMapping[contract_counterparties[0].counterparty_id]?.name} - ${
      idToCpMapping[contract_counterparties[1].counterparty_id]?.name
    }`;

    if (name) return { label: name, secondaryLabel: cpsLabel };

    return {
      secondaryLabel: id,
      label: cpsLabel,
    };
  };
}

export function useCurrentMarginGroups(cpId?: string) {
  const { cpId: defaultCpId } = useParams();
  return useMarginGroups(cpId || defaultCpId);
}

export function useDefaultMarginGroup(cpId?: string) {
  const { data = [] } = useCurrentMarginGroups(cpId);
  return data[0];
}

export function useMarginGroupData(targetCpId?: string, sourceCpId?: string) {
  const { cpId: defaultCpId } = useParams();

  const cpId = sourceCpId || defaultCpId;

  const { data: accountGroups = [], isLoading: isLoadingAccountGroups } = useMarginGroups(cpId);
  const { data: targetAccountGroups = [], isLoading: isLoadingTargetAccountGroups } = useMarginGroups(targetCpId);

  const isLoading = isLoadingAccountGroups || isLoadingTargetAccountGroups;

  return {
    accountGroup: accountGroups[0],
    targetAccountGroup: targetAccountGroups[0],
    isLoading,
  };
}

export function useTargetCounterpartyData(contractType: ContractType, targetCpId?: string) {
  const { cpId } = useParams();
  const { data = [], isLoading: isLoadingContracts, dataUpdatedAt } = useContracts(contractType, cpId);

  const { accountGroup, targetAccountGroup, isLoading: isLoadingAccountGroups } = useMarginGroupData(targetCpId);

  const isLoading = isLoadingContracts || isLoadingAccountGroups;

  const contract = useMemo(() => {
    return data?.find((d) => {
      return d.contract_counterparties.find((c) => {
        return c.counterparty_id === targetCpId;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCpId, dataUpdatedAt]);

  const contracts = useMemo(() => {
    return targetCpId
      ? data?.filter((d) => {
          return d.contract_counterparties.some((c) => {
            return c.counterparty_id === targetCpId;
          });
        })
      : data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCpId, dataUpdatedAt]);

  return {
    contract,
    contracts,
    accountGroup,
    targetAccountGroup,
    isLoading,
  };
}
